<script lang="ts" context="module">
	export type Accessory = TrexHolsterAccessory & TrexHolsterAccessoryVariation & {
		purchasable: boolean
		waitlist_ref?: number | null
		waitlist_visible_on_storefront?: TrexStockItem[`waitlist_visible_on_storefront`]
		waitlist_is_full?: boolean
		stock_item_image_url: TrexStockItem[`image_url`] | null
		attributes: Array<{ is_default: 1 | 0 }
			& Pick<TrexHolsterAccessoryAttribute, `name` | `holster_accessory_attribute_ref` | `controlled_by_holster_input_property`>
			& Pick<TrexHolsterAccessoryAttributeValue, `value` | `holster_accessory_attribute_value_ref` | `woocommerce_slug`>
		>
		holster_accessory_colors: TrexHolsterAccessoryColor[]
		colors: TrexColor[]
		sibling_variation_may_be_attached_to_holster: boolean
	}
</script>

<script lang="ts">
	import type { AllHolsterAccessoryAttributes, SelectedAttributes } from 'shared/bzns/holster/accessories'
	import type { Writable } from 'svelte/store'

	import { createEventDispatcher } from 'svelte'
	import { image_root } from 'shared/bzns/holster/image_paths'
	import { get_accessory_image_file } from 'shared/bzns/holster/get_image_file'
	import DropdownColorPicker from './DropdownColorPicker.svelte'
	import ExternalLinkIcon from './ExternalLinkIcon.svelte'
	import Waitlist from './Waitlist.svelte'
	import Select from 'shared/component/Select.svelte'
	import InputTable from 'shared/component/InputTable.svelte'
	import Label from 'shared/component/StorefrontLabel.svelte'
	import HolsterImage from 'shared/component/HolsterImage.svelte'

	const dispatch = createEventDispatcher()
	const image_size = 80

	const get_small_accessory_image_path = (
		color_image_file_name: string,
		accessory_image_file_name: string
	) => {
		const image_path_parts = [
			image_root,
			holster_line_image_file_name_string,
			get_accessory_image_file({
				color_image_file_name,
				accessory_image_file_name,
				holster_line_name: holster_line_image_file_name_string.split(`/`)[0],
			}),
		]

		return image_path_parts.join(``)
	}

	const get_belt_loop_image_path = (
		accessory_image_file_name: string,
		belt_width_attribute_value: string | undefined
	) => {
		const belt_loop_color_image_file_name = `00016` // Black

		const image_path_parts = [
			image_root,
			holster_line_image_file_name_string,
			get_accessory_image_file({
				color_image_file_name: belt_loop_color_image_file_name,
				accessory_image_file_name,
				belt_loop_size: belt_width_attribute_value === `1.5"` ? `1.5"` : `1.75"`,
				holster_line_name: holster_line_image_file_name_string.split(`/`)[0],
			}),
		]

		return image_path_parts.join(``)
	}

	export let accessories_with_purchasability: Accessory[]
	export let accessory: Accessory
	export let holster_line_image_file_name_string: TrexHolsterLine[`image_file_name_string`]
	export let holster_line_image_position_algorithm: TrexHolsterLine[`image_position_algorithm`]
	export let promo_active: boolean
	export let checked: boolean
	export let holster_accessory_ref_to_color_ref: Map<number, number>
	export let attributes: AllHolsterAccessoryAttributes
	export let selected_attributes: SelectedAttributes
	export let selected_holster_properties: Writable<{ [key: string]: unknown }>

	let selected_color = accessory.colors.find(color => color.color_ref === holster_accessory_ref_to_color_ref.get(accessory.holster_accessory_ref)) || null

	$: default_color_ref = accessory.holster_accessory_colors.find(({ is_default }) => is_default)?.color_ref
	$: default_color = accessory.colors.find(color => color.color_ref === default_color_ref) || accessory.colors[0]

	$: color_image_file_name = (selected_color || default_color)?.image_file_name_string

	$: color_ref_to_additional_price = new Map(accessory.holster_accessory_colors.map(hac => [ hac.color_ref, hac.additional_price ]))

	$: selected_attribute_value_refs = new Set(Object.values(selected_attributes))

	$: belt_width_attribute_value = Object.values(attributes)
		.flatMap(({ values }) => Object.values(values))
		.find(attr => attr.name === `Belt Width` && selected_attribute_value_refs.has(attr.holster_accessory_attribute_value_ref))?.value

	$: belt_loop_image_url = accessory.has_belt_loops && get_belt_loop_image_path(accessory.image_file_name, belt_width_attribute_value)

	$: checked && on_checked_change()

	const on_checked_change = () => {
		if (checked && !selected_color && default_color) {
			selected_color = default_color
		}
	}

	$: selected_color && on_selected_color_change()

	const on_selected_color_change = () => {
		if (selected_color) {
			holster_accessory_ref_to_color_ref.set(accessory.holster_accessory_ref, selected_color.color_ref)
			holster_accessory_ref_to_color_ref = holster_accessory_ref_to_color_ref
		}
	}

	const sort_colors = (colors: TrexColor[]) => colors.sort((a, b) => {
		if (a.sort_order === b.sort_order) { return 0 }
		// Put colors without sort order at the end
		if (!a.sort_order) { return 1 }
		if (!b.sort_order) { return -1 }
		return a.sort_order - b.sort_order
	})

	const accessory_has_color_attribute = accessory.attributes?.some(({ holster_accessory_attribute_ref }) => attributes[holster_accessory_attribute_ref].name.toLowerCase() === `color`)

	let controls_width
	let color_dropdown_width

	const does_accessory_exist_for_selection = (theoretical_selection: SelectedAttributes) => accessories_with_purchasability
		.some(acc => Object.values(theoretical_selection)
			.every(holster_attribute_value_ref => acc.attributes
				.some(attr => attr.holster_accessory_attribute_value_ref === holster_attribute_value_ref)
			)
		)
</script>

<div class=accessory>
	<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
	<div
		class="accessory_img"
		style="--image-size: {image_size}px;"
		on:click={() => dispatch(`toggle`, accessory)}
	>
		<HolsterImage
			image_url={accessory.image_file_name
				? get_small_accessory_image_path(color_image_file_name, accessory.image_file_name)
				: accessory.stock_item_image_url || ``
			}
			belt_loop_image_url={belt_loop_image_url || null}
			image_alt={(selected_color && !accessory_has_color_attribute ? selected_color.name + ` ` : ``) + accessory.name}
			image_position_algorithm={holster_line_image_position_algorithm}
			is_accessory={true}
			width={image_size}
			flip_x={accessory.preview_flip_x}
			is_attached_to_holster={accessory.sibling_variation_may_be_attached_to_holster}
		/>
	</div>
	<div class="accessory_details" bind:clientWidth={controls_width}>
		<div>
			<div class=name>
				{accessory.name}
				<a
					href="/?p={accessory.product_ref}"
					target="_blank"
					style="padding: 4px;"
				><ExternalLinkIcon /></a>
			</div>
			<div>
				{#if !accessory.purchasable}
					{#if accessory.waitlist_visible_on_storefront && accessory.waitlist_ref}
						<Waitlist
							waitlist_ref={accessory.waitlist_ref}
							waitlist_is_full={accessory.waitlist_is_full}
						/>
					{:else}
						(unavailable)
					{/if}
				{/if}
			</div>
		</div>

		<InputTable>
			<tbody>
				{#if accessory.attributes}
					{#each accessory.attributes.sort((a, b) => b.name.localeCompare(a.name)) as attribute (attribute.holster_accessory_attribute_ref)}
						{@const controlled_by_holster_input_property = attributes[attribute.holster_accessory_attribute_ref].controlled_by_holster_input_property}

						{#if !(controlled_by_holster_input_property && $selected_holster_properties[controlled_by_holster_input_property])}
							<tr>
								<th><Label for={attribute.holster_accessory_attribute_ref}>{attribute.name}</Label></th>
								<td>
									<Select
										placeholder={attribute.name}
										bind:value={selected_attributes[attribute.holster_accessory_attribute_ref]}
										id={attribute.holster_accessory_attribute_ref}
									>
										{#each Object.values(attributes[attribute.holster_accessory_attribute_ref].values) as { holster_accessory_attribute_value_ref, value }}
											<option
												value={holster_accessory_attribute_value_ref}
												disabled={!does_accessory_exist_for_selection({
													...selected_attributes,
													[attribute.holster_accessory_attribute_ref]: holster_accessory_attribute_value_ref,
												})}
											>{ value }</option>
										{/each}
									</Select>
								</td>
							</tr>
						{/if}
					{/each}
				{/if}
				<!-- hide swatches if waitlist form is visible, just to save room -->
				<!-- hide if accessory has color attribute, but a placeholder holster_accessory_color is still auto-selected -->
				{#if !accessory_has_color_attribute && (accessory.purchasable || !accessory.waitlist_visible_on_storefront)}
					<tr>
						<th><Label for={accessory.holster_accessory_variation_ref}_color>Color</Label></th>
						<td><div bind:clientWidth={color_dropdown_width}><DropdownColorPicker
							id={accessory.holster_accessory_variation_ref}_color
							bind:selected_color
							colors={sort_colors(accessory.colors)}
							{color_ref_to_additional_price}
							{promo_active}
							promo_discount_rate={accessory.promo_discount_rate}
							promo_discount_usd={accessory.promo_discount_usd}
							color_image_min_size={40}
							color_image_max_size={48}
							style="
								--dropdown-width: {controls_width}px;
								--dropdown-left: calc({color_dropdown_width}px - {controls_width}px);
							"
						/></div></td>
					</tr>
				{/if}
			<tbody>
		</InputTable>
	</div>
</div>

<style>
	.accessory {
		display: flex;
		flex-direction: row;
		gap: 8px;
		padding-bottom: 8px; /* to reduce transition:slide jumping */
	}

	.accessory_img {
		width: var(--image-size);
		height: var(--image-size);
		border-radius: 3px;
		overflow: hidden;
	}

	.accessory_details {
		display: flex;
		flex-direction: column;
		gap: 4px;
		flex-grow: 1;
	}

	.name {
		font-family: 'Protipo Narrow';
		color: white;
		text-transform: capitalize;
	}

	@media(max-width: 420px) {
		.name {
			font-size: 14px;
		}
		.accessory :global(table), .accessory :global(tr), .accessory :global(th), .accessory :global(td) {
			display: block;
			width: 100%;
			padding: 0;
			--dropdown-left: 0 !important;
		}
	}
</style>
